"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.summarySentSchema = exports.summaryReadSchema = exports.summaryListSchema = exports.summaryFacilitySchema = exports.summarySchema = exports.summaryParamsSchema = exports.summaryListQuerySchema = void 0;
/**
 * `RPM-103` — a building's daily and weekly summaries, prepared by ChartVN and sent by a nurse
 * (`CR-43`, `J-489`).
 *
 * A summary falls due at a local time the building's routing sets and is created, once, the next time
 * the summaries are read. ChartVN sends nothing: the nurse copies the prepared text into her own mail,
 * sends it to the recipients shown, and marks it sent. Marking it sent records who, when and the
 * recipients as used, discharges the summary step of every entry it carried (`RPM-042`), and makes
 * the summary final.
 *
 * The block's API row: `GET /api/rpm/v1/summaries?facilityId=…`, `GET /api/rpm/v1/summaries/:summaryId`
 * and `POST /api/rpm/v1/summaries/:summaryId/sent`. Only a building's id, words from closed lists and
 * dates travel in the query; the prepared text names residents and travels in the response body alone.
 *
 * THE LIST IS FILTERED AND PAGED AS THE WORKLIST IS (`CR-45` R5, `J-602`): Building, Kind, State, a
 * due-date range, a sent-date range and Sent by me, each date read on each building's own calendar.
 * With no date filter it lists what it always listed: every summary still due, and those due in the
 * last 14 days.
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/** A calendar date, read on each building's own calendar. */
const LOCAL_DATE = zod_1.z.iso.date();
exports.summaryListQuerySchema = zod_1.z
    .strictObject({
    /** Without one, every building the account reaches. */
    facilityId: MIRROR_ID.optional(),
    kind: zod_1.z.enum(rpm_enums_js_1.summaryKind.enumValues).optional(),
    state: zod_1.z.enum(rpm_enums_js_1.summaryState.enumValues).optional(),
    /** The due-date range, both ends inclusive, as each building's local date. */
    dueFrom: LOCAL_DATE.optional(),
    dueTo: LOCAL_DATE.optional(),
    /** The sent-date range, both ends inclusive, as each building's local date. */
    sentFrom: LOCAL_DATE.optional(),
    sentTo: LOCAL_DATE.optional(),
    /** `me`: only the summaries the signed-in account marked sent. */
    sentBy: zod_1.z.enum(["me"]).optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
    /** Opaque. The server issues it and the server reads it. */
    cursor: zod_1.z.string().min(1).max(200).optional(),
})
    .refine((q) => q.dueFrom === undefined || q.dueTo === undefined || q.dueFrom <= q.dueTo, {
    message: "dueFrom is after dueTo.",
    path: ["dueFrom"],
})
    .refine((q) => q.sentFrom === undefined || q.sentTo === undefined || q.sentFrom <= q.sentTo, {
    message: "sentFrom is after sentTo.",
    path: ["sentFrom"],
})
    .meta({ description: "The buildings whose summaries are read, the filters and the page" });
exports.summaryParamsSchema = zod_1.z.object({ summaryId: zod_1.z.uuid() });
/** One address a summary goes to, with the label the directory or the standing copy gives it. */
const summaryRecipientSchema = zod_1.z.object({ value: zod_1.z.string(), label: zod_1.z.string() });
exports.summarySchema = zod_1.z
    .object({
    rpmFacilitySummaryId: zod_1.z.uuid(),
    facilityId: MIRROR_ID,
    kind: zod_1.z.enum(rpm_enums_js_1.summaryKind.enumValues),
    /** The scheduled local time, as an instant. */
    dueAt: zod_1.z.iso.datetime({ offset: true }),
    /** The entries it covers were opened in `[windowStart, windowEnd)`. */
    windowStart: zod_1.z.iso.datetime({ offset: true }),
    windowEnd: zod_1.z.iso.datetime({ offset: true }),
    state: zod_1.z.enum(rpm_enums_js_1.summaryState.enumValues),
    sentAt: zod_1.z.iso.datetime({ offset: true }).nullable(),
    sentBy: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string().nullable() }).nullable(),
    /**
     * Who it goes to: as used, once it is sent; as the directory stands now, while it is due. `to` is
     * the building's addresses, `cc` the tenant's `SUMMARY` standing copies.
     */
    recipients: zod_1.z.object({
        to: zod_1.z.array(summaryRecipientSchema),
        cc: zod_1.z.array(summaryRecipientSchema),
    }),
    /** The prepared subject line. */
    subject: zod_1.z.string(),
    /** The prepared text, read-only: the content lines, then one block per entry. */
    text: zod_1.z.string(),
    /** The entries it lists, in the order the text lists them. */
    entries: zod_1.z.array(zod_1.z.object({
        rpmAlertId: zod_1.z.uuid(),
        /** Whether the entry's routing sends it to this summary: its summary step is discharged. */
        routedToSummary: zod_1.z.boolean(),
    })),
})
    .meta({ id: "FacilitySummary" });
/**
 * One building's summaries: its schedule, then those of its summaries on this page. A building the
 * list covers is listed even where none of its summaries is on the page.
 */
exports.summaryFacilitySchema = zod_1.z.object({
    facilityId: MIRROR_ID,
    facilityName: zod_1.z.string().nullable(),
    /** The building's operative zone: every time on the screen is read on its wall clock. */
    zone: zod_1.z.string(),
    schedule: zod_1.z.object({
        dailyTimes: zod_1.z.array(zod_1.z.string()),
        /** 0 Sunday … 6 Saturday; null with `weeklyTime` where no weekly summary is set. */
        weeklyWeekday: zod_1.z.number().int().min(0).max(6).nullable(),
        weeklyTime: zod_1.z.string().nullable(),
    }),
    summaries: zod_1.z.array(exports.summarySchema),
});
/**
 * One page of the list. The page is the first `limit` summaries the filters match across every
 * building listed, latest due first, then daily before weekly, then by id; each is under its building.
 */
exports.summaryListSchema = zod_1.z
    .object({
    facilities: zod_1.z.array(exports.summaryFacilitySchema),
    /** The value to pass as `cursor` for the next page; null on the last page. */
    nextCursor: zod_1.z.string().nullable(),
    /** Every summary the filters match, across pages, in the buildings listed. */
    total: zod_1.z.number().int().nonnegative(),
})
    .meta({ description: "One page of the buildings' summaries" });
/** One summary on its own screen, with the building it belongs to. Reading it creates nothing. */
exports.summaryReadSchema = zod_1.z
    .object({
    facilityId: MIRROR_ID,
    facilityName: zod_1.z.string().nullable(),
    /** The building's operative zone: every time on the screen is read on its wall clock. */
    zone: zod_1.z.string(),
    summary: exports.summarySchema,
})
    .meta({ description: "One summary, prepared, with its building" });
exports.summarySentSchema = zod_1.z
    .object({
    summary: exports.summarySchema,
    /** How many entries' summary steps the send discharged: the announcement's number. */
    discharged: zod_1.z.number().int().nonnegative(),
})
    .meta({ description: "The summary marked sent, and how many entries it discharged" });
//# sourceMappingURL=rpm-summaries.js.map